import "./SnackBars.css";
import { forwardRef, useCallback, useEffect } from "react";
import { useSnackbar, SnackbarContent, CustomContentProps } from "notistack";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import IconButton from "@mui/material/IconButton";
import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import useStore from "../../Store/Store";
import { theme } from "../../Themes/Themes";
import { Tooltip } from "@mui/material";
import SimulationControlButtons from "../../Components/SourceDoc/TabViews/SimulationsTab/SimulationControlButtons";
import { useResponsiveValue, inSimulationSnackBarConfig } from "../responsive";

interface InSimulationSnackBarProps extends CustomContentProps {}

const InSimulationSnackBar = forwardRef<
  HTMLDivElement,
  InSimulationSnackBarProps
>(({ id, ...props }, ref) => {
  const { closeSnackbar } = useSnackbar();
  const { isSimulationFullSizeMode, isRunningSimulation } = useStore(
    (state) => ({
      isSimulationFullSizeMode: state.isSimulationFullSizeMode,
      isRunningSimulation: state.isRunningSimulation,
    })
  );

  // Get responsive positioning values
  const responsiveConfig = useResponsiveValue(inSimulationSnackBarConfig);

  const handleDismiss = useCallback(() => {
    closeSnackbar(id);
    useStore.getState().setSelectedSimulationKey("");
  }, [id, closeSnackbar]);

  // Determine styling based on whether simulation is running
  const isRunningStyle = isRunningSimulation
    ? {
        backgroundColor: "transparent",
        border: `1px solid ${theme.custom.lighterPink}`,
        color: theme.custom.lighterPink,
        background: "transparent",
        boxShadow: "none",
      }
    : {
        backgroundColor: "transparent",
        color: "#fffffffc",
        background: `${theme.custom.pink}`,
        boxShadow: "1px 1px 5px 0px rgba(0,0,0,0.1)",
      };

  // Calculate responsive top position
  const getTopPosition = () => {
    return isSimulationFullSizeMode
      ? responsiveConfig.fullSizeMode
      : responsiveConfig.normalMode;
  };

  return (
    <SnackbarContent
      ref={ref}
      className="inSimulationSnackBar"
      style={{
        top: getTopPosition(),
      }}
    >
      <Card
        className="snackBarCard"
        style={{
          position: "relative",
          borderRadius: "30px",
          width: isSimulationFullSizeMode ? "350px" : "200px", // Wider to accommodate buttons
          height: "50px", // Height to accommodate the control buttons
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          ...isRunningStyle, // Apply conditional styling
          background: isSimulationFullSizeMode
            ? "rgba(255, 255, 255, 0.4)"
            : theme.custom.lighterPink,
        }}
      >
        <CardActions
          style={{
            position: "relative",
            width: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "8px 12px", // Added padding for better spacing
          }}
        >
          <Typography
            variant="body2"
            style={{
              position: "relative",
              paddingLeft: "20px",
              height: "100%",
              verticalAlign: "middle",
              color: isRunningSimulation
                ? theme.custom.lighterPink
                : "#fffffffc", // Conditional text color
            }}
          >
            {props.message}
          </Typography>
          {isSimulationFullSizeMode && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginLeft: "10px",
              }}
            >
              <SimulationControlButtons />
            </div>
          )}
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              marginLeft: "8px",
            }}
          >
            <Tooltip title="Exit simulation mode">
              <IconButton
                color="primary"
                onClick={handleDismiss}
                style={{
                  backgroundColor: "#ffffff",
                  border: `1px solid ${theme.custom.pink}`,
                  color: theme.custom.pink,
                  width: "30px",
                  height: "30px",
                  padding: "6px",
                  borderRadius: "50%",
                  boxShadow: isRunningSimulation
                    ? "none"
                    : "0px 2px 4px rgba(0, 0, 0, 0.1)",
                }}
              >
                <ExitToAppIcon style={{ width: "18px", height: "18px" }} />
              </IconButton>
            </Tooltip>
          </div>
        </CardActions>
      </Card>
    </SnackbarContent>
  );
});

InSimulationSnackBar.displayName = "InSimulationSnackBar";

export default InSimulationSnackBar;
